import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { Entity, type EntityCtx, type UnitOrder } from './Entity';
import { Scene as GameScene } from './Scene';
import { Input } from './Input';
import { runCollisions } from './Collisions';
import { PlayerPlane } from './entities/PlayerPlane';
import { EnemyPlane, type EnemyType } from './entities/EnemyPlane';
import { Zeppelin } from './entities/Zeppelin';
import { SkyCarrier } from './entities/SkyCarrier';
import { Tank } from './entities/Tank';
import { TownGunner } from './entities/TownGunner';
import { AlliedTank } from './entities/AlliedTank';
import { Juggernaut } from './entities/Juggernaut';
import { AetherDreadnought } from './entities/AetherDreadnought';
import { ClockworkColossus } from './entities/ClockworkColossus';
import { TeslaBastion } from './entities/TeslaBastion';
import { IronWorm } from './entities/IronWorm';
import { MaelstromEngine } from './entities/MaelstromEngine';
import { WaveDirector } from '../waves/WaveDirector';
import { Explosion } from './entities/Explosion';
import { Ufo } from './entities/Ufo';
import { Bullet } from './entities/Bullet';
import { RandomEventDirector } from './RandomEvents';
import { simClock } from './clock';
import { useStore, type FriendlyKind, type MapData } from '../state/store';
import { Audio } from './Audio';

/** How strongly the baked sky environment reflects/ambient-fills materials. Low so metals
 *  get a touch of sky sheen WITHOUT flooding flat ambient that washes out the sun shading. */
const ENV_INTENSITY = 0.3;

/** Soft cinematic vignette applied as the final post pass (operates on the tonemapped sRGB
 *  image). Gently darkens the far corners to focus the eye — kept subtle so it isn't murky. */
const VIGNETTE_SHADER = {
  uniforms: { tDiffuse: { value: null as THREE.Texture | null }, strength: { value: 0.9 }, amount: { value: 0.22 } },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform float strength;
    uniform float amount;
    varying vec2 vUv;
    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      vec2 d = vUv - 0.5;
      float vig = smoothstep(0.9, 0.18, dot(d, d) * strength);
      c.rgb *= mix(1.0, vig, amount);
      gl_FragColor = c;
    }`,
};

export class Game {
  readonly canvas: HTMLCanvasElement;
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly clock = new THREE.Clock();
  readonly gameScene: GameScene;
  readonly input: Input;
  readonly audio: Audio;
  readonly director: WaveDirector;
  readonly randomEvents = new RandomEventDirector();

  entities: Entity[] = [];
  /** Friendly units lost this run, oldest first — reinforcements replace whatever died first.
   *  Recorded at the dead-sweep; consumed by WaveDirector.deliverReinforcement(). */
  lostUnits: { kind: FriendlyKind; label: string; pos: THREE.Vector3; elevate: number }[] = [];
  /** The unit the player is currently controlling (camera + input). */
  controlled!: Entity;
  /** Back-compat alias used by enemy targeting / audio — always the controlled unit. */
  get player(): Entity { return this.controlled; }
  /** True while the player is holding the manual-fire trigger (gamepad RT or the touch FIRE
   *  button). The controlled unit fires only on this; AI wingmen/crews still auto-fire. */
  fireHeld(): boolean { return this.input.gamepadFireHeld() || this.input.keyFireHeld() || useStore.getState().manualFiring; }
  camera!: THREE.PerspectiveCamera;
  private composer!: EffectComposer;
  private renderPass!: RenderPass;
  private bloomPass: UnrealBloomPass | null = null;
  private fxaaPass?: ShaderPass;
  private running = false;
  // Audio-event watchers (compared each frame against store/world state).
  private _prevKills = 0;
  private _villageAlarmAt = -999;   // last time the under-attack klaxon played (debounce)
  private _lastVillageDamage = -999; // freshest village-damage timestamp we've already reacted to

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.gameScene = new GameScene(this.scene);
    // Image-based lighting: bake the sky (+ ground ambiance) into an environment map so
    // every metal surface — cowlings, guns, brass, armour — picks up real sky reflections
    // and reads as actual material instead of flat shading.
    this.bakeEnvironment();
    this.input = new Input(canvas);
    this.audio = new Audio();
    this.director = new WaveDirector(this);

    this.setupCameraAndPlayer();
    this.buildPostFX();
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    // GPU context loss (driver reset, aggressive tab eviction): preventDefault permits restore,
    // pause the loop meanwhile, and show a small banner so a frozen frame isn't a mystery.
    // three re-uploads its GPU resources automatically once the context comes back.
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.running = false;
      this._ctxLostBanner = document.createElement('div');
      this._ctxLostBanner.textContent = '⚠ GRAPHICS PAUSED — RECOVERING…';
      this._ctxLostBanner.style.cssText =
        'position:fixed;top:40%;left:50%;transform:translate(-50%,-50%);padding:12px 18px;' +
        'background:rgba(0,0,0,0.8);color:#ffd24a;font:700 14px ui-monospace,monospace;' +
        'letter-spacing:2px;border:1px solid rgba(255,210,74,0.5);border-radius:6px;z-index:99';
      document.body.appendChild(this._ctxLostBanner);
    });
    canvas.addEventListener('webglcontextrestored', () => {
      this._ctxLostBanner?.remove();
      this._ctxLostBanner = null;
      if (!this.running) { this.running = true; this.clock.getDelta(); requestAnimationFrame(this.loop); }
    });
  }

  private _ctxLostBanner: HTMLDivElement | null = null;

  /** PMREM-bake the current sky into scene.environment for IBL reflections + soft ambient. */
  private bakeEnvironment() {
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    pmrem.compileEquirectangularShader();
    // far must reach past the sky dome (scaled 40000) so the bake captures sky, not black.
    const envRT = pmrem.fromScene(this.scene, 0, 10, 50000);
    this.scene.environment = envRT.texture;
    pmrem.dispose();
    this.applyEnvIntensity(this.scene); // dial back IBL on the static world (terrain, mountains…)
  }

  /** Clamp envMapIntensity on every standard material under `root` so the baked sky adds a
   *  subtle metal sheen rather than a flat ambient flood. Called on the world once and on
   *  every entity as it's added (the single choke point for spawned planes/tanks/etc.). */
  private applyEnvIntensity(root: THREE.Object3D) {
    root.traverse((o) => {
      const mat = (o as THREE.Mesh).material;
      if (!mat) return;
      const list = Array.isArray(mat) ? mat : [mat];
      for (const m of list) {
        if ((m as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
          (m as THREE.MeshStandardMaterial).envMapIntensity = ENV_INTENSITY;
        }
      }
    });
  }

  /** Build the post-processing chain: HDR scene → bloom (glow on sun/fire/tracers/brass) →
   *  ACES tonemap + sRGB → a soft cinematic vignette. Edge AA is FXAA (a ~1ms post pass) rather
   *  than 4x MSAA: GPU profiling on this machine's iGPU showed MSAA cost ~11ms/frame (the single
   *  biggest frame-time item, ~32→50 fps when removed) because multisampling+resolving a HalfFloat
   *  HDR target is very fill-heavy, while FXAA gives clean edges for a tenth of that. */
  private buildPostFX() {
    const dpr = this.renderer.getPixelRatio();
    const w = window.innerWidth, h = window.innerHeight;
    // Profiling overrides (URL flags): ?msaa=4 re-enables MSAA, ?nobloom=1, ?novig=1, ?nofxaa=1.
    const qp = (typeof location !== 'undefined') ? new URLSearchParams(location.search) : new URLSearchParams();
    const samples = qp.get('msaa') === '4' ? 4 : 0; // default: no MSAA (FXAA does the AA)
    const target = new THREE.WebGLRenderTarget(w * dpr, h * dpr, { type: THREE.HalfFloatType, samples });
    this.composer = new EffectComposer(this.renderer, target);
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(w, h);

    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    // Gentle bloom — only the genuinely bright stuff (sun, fire, tracers) glows; high
    // threshold keeps the daylit sky/terrain from blooming and washing out. Held in a field so
    // the FX-quality setting can flip it live (pass.enabled — the composer skips disabled passes).
    if (qp.get('nobloom') !== '1') {
      this.bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), 0.35, 0.4, 0.9);
      this.bloomPass.enabled = !useStore.getState().fxLite;
      this.composer.addPass(this.bloomPass);
    }

    this.composer.addPass(new OutputPass());

    // FXAA edge smoothing (replaces MSAA). Skipped only if MSAA was forced back on, or ?nofxaa=1.
    if (samples === 0 && qp.get('nofxaa') !== '1') {
      const fxaa = new ShaderPass(FXAAShader);
      fxaa.material.uniforms.resolution.value.set(1 / (w * dpr), 1 / (h * dpr));
      this.composer.addPass(fxaa);
      this.fxaaPass = fxaa;
    }

    if (qp.get('novig') !== '1') this.composer.addPass(new ShaderPass(VIGNETTE_SHADER));
  }

  private setupCameraAndPlayer() {
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.5, 20000);
    if (this.renderPass) this.renderPass.camera = this.camera; // keep post-FX on the fresh camera after restart
    const plane = new PlayerPlane();
    this.add(plane);
    // Spawn BELOW the cloud band (sprites centre at y 10..220) so the opening seconds show the
    // battlefield, not the inside of a cloud. ~350u above the terrain (-468) — battle altitude.
    plane.position.set(0, -120, 0);
    plane.possess(this.camera);     // the player starts in this fighter
    this.controlled = plane;
    this.announceUnit(plane);
    // Start normal runs with a small squadron of AI wingmen. The authored training sortie omits
    // them so its scripted targets cannot be cleared by an unseen helper before the player acts.
    if (!useStore.getState().tutorial) {
      for (let i = 0; i < 2; i++) {
        const a = (i / 2) * Math.PI * 2;
        this.spawnAlliedFighter(new THREE.Vector3(Math.cos(a) * 360, -90 + i * 20, Math.sin(a) * 360));
      }
    }
    this.deployGroundForces();
  }

  /** Place fixed AA emplacements over the towns nearest the centre of the map, plus a
   *  couple of roaming allied tanks — the ground half of the force the player commands. */
  private deployGroundForces() {
    const villages = [...this.gameScene.villages]
      .sort((a, b) => a.position.lengthSq() - b.position.lengthSq());
    const gunnerTowns = villages.slice(0, 5);
    gunnerTowns.forEach((v, i) => {
      // Offset the gun just outside the built-up area so it isn't buried in rooftops.
      const off = new THREE.Vector3(Math.cos(i) * (v.radius + 20), 0, Math.sin(i) * (v.radius + 20));
      this.spawnTownGunner(v.position.clone().add(off), `AA GUN ${i + 1}`);
    });
    for (let i = 0; i < 2; i++) {
      const a = (i / 2) * Math.PI * 2 + 0.6;
      this.spawnAlliedTank(new THREE.Vector3(Math.cos(a) * 700, 0, Math.sin(a) * 700), `TANK ${i + 1}`);
    }
    // Trench garrison — MG/AA emplacements manning the front line. They engage enemy tanks and
    // low aircraft, can be possessed from the map, and turn the trench into a kill-zone: tanks
    // bog down crossing the parapets while the garrison and the player thin them.
    // Elevated ~7u so the gun + gunner's eye clear the trench's earth parapets (the view down the
    // line of the trench is no longer blocked by the surrounding earthwork).
    this.gameScene.trenchGarrisonPoints(4).forEach((p, i) => this.spawnTownGunner(p, `TRENCH MG ${i + 1}`, 7));
  }

  spawnTownGunner(pos: THREE.Vector3, label: string, elevate = 0) {
    const g = new TownGunner(pos, label, elevate);
    this.add(g);
    return g;
  }

  spawnAlliedTank(pos: THREE.Vector3, label: string) {
    const t = new AlliedTank(pos, label);
    this.add(t);
    return t;
  }

  /** Allied AI fighter — flies on autopilot hunting enemies; the player can jump into it.
   *  German Albatros with iron crosses (the player's side is the Red Baron's), in a
   *  Jasta green so it's instantly distinct from the red triplane the player flies and the
   *  khaki, roundel-marked Entente enemies. Cross = friend, roundel = foe. */
  spawnAlliedFighter(pos: THREE.Vector3) {
    const f = new PlayerPlane({ id: 'albatros', cowling: 0x2f6e4f, wing: 0x46946c });
    f.unitLabel = 'FIGHTER';
    this.add(f);
    f.position.copy(pos);
    f.group.lookAt(0, pos.y, 0);
    return f;
  }

  /** All live allied units the player can jump to. */
  controllables(): Entity[] {
    return this.entities.filter((e) => e.controllable && e.friendly && !e.dead && e.alive());
  }

  /** Live friendly fighters (planes), controlled + AI. Used by the wave director to keep
   *  the squadron topped up so there's always a wingman to jump into. */
  alliedFighterCount(): number {
    return this.entities.filter((e) => e.kind === 'player' && !e.dead && e.alive()).length;
  }

  /** Nearest live friendly *plane* to a point — what enemy planes dogfight (air-to-air).
   *  Ground allies (gunners, tanks) are engaged by ground enemies / bombers instead, so
   *  enemy fighters don't fixate on a target they can't dive on. Falls back to the
   *  controlled unit. */
  nearestFriendlyPlane(pos: THREE.Vector3): Entity {
    let best: Entity | null = null;
    let bestD = Infinity;
    for (const e of this.entities) {
      if (e.kind !== 'player' || e.dead || !e.alive()) continue;
      const d = e.position.distanceToSquared(pos);
      if (d < bestD) { bestD = d; best = e; }
    }
    return best ?? this.controlled;
  }

  /** Look up a live friendly by id (enemy target tracking with hysteresis). */
  friendlyById(id: number): Entity | null {
    const e = this.entities.find((x) => x.id === id);
    return e && e.friendly && !e.dead && e.alive() ? e : null;
  }

  /** Nearest live friendly GROUND unit (allied tank or AA/trench gun) within `maxDist` of `pos`,
   *  or null. Used by enemy ground forces (tanks) and strafing bombers to engage the player's
   *  ground defences, not just the villages. */
  nearestAllyGround(pos: THREE.Vector3, maxDist: number): Entity | null {
    let best: Entity | null = null;
    let bestD = maxDist * maxDist;
    for (const e of this.entities) {
      if (e.kind !== 'ally' || e.dead || !e.alive()) continue;
      const d = e.position.distanceToSquared(pos);
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }

  /** Nearest live friendly of ANY kind (player planes, AA guns, tanks) within `maxDist` — what a
   *  boss's defensive guns shoot at. Bosses fight the army, not just the player: armor pushed onto
   *  a boss draws its fire. Null when nothing's in range. */
  nearestFriendly(pos: THREE.Vector3, maxDist: number): Entity | null {
    let best: Entity | null = null;
    let bestD = maxDist * maxDist;
    for (const e of this.entities) {
      if (e.dead || !e.alive()) continue;
      if (e.kind !== 'player' && !(e.kind === 'ally' && e.friendly)) continue;
      const d = e.position.distanceToSquared(pos);
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }

  /** Jump straight to whichever unit is best placed to deal with the most urgent threat.
   *  Threat = the most-recently-damaged village (bombing in progress) or friendly unit.
   *  Picks the controllable nearest the threat, excluding the one already controlled. */
  jumpToCrisis() {
    const store = useStore.getState();
    // The opening sortie's bomber lesson is deliberately role-specific: put the player in the
    // AA emplacement assigned to the scripted village. The generic nearest-unit rule can choose
    // a tank on some force layouts, leaving the lesson stuck in the wrong seat.
    if (store.tutorial && store.tutorialDone.scout && !store.tutorialDone.gun) {
      const village = this.gameScene.villages[this._tutVillageIndex];
      const guns = this.controllables().filter((e): e is TownGunner => e instanceof TownGunner);
      if (village && guns.length > 0) {
        let best = guns[0];
        let bestD = best.position.distanceToSquared(village.position);
        for (const gun of guns.slice(1)) {
          const d = gun.position.distanceToSquared(village.position);
          if (d < bestD) { best = gun; bestD = d; }
        }
        this.takeControl(best);
        return;
      }
    }
    const threat = this.pickThreatPoint();
    if (!threat) return;
    const units = this.controllables().filter((e) => e !== this.controlled);
    if (units.length === 0) return;
    let best = units[0];
    let bestD = Infinity;
    for (const u of units) {
      const d = u.position.distanceToSquared(threat.pos);
      if (d < bestD) { bestD = d; best = u; }
    }
    this.takeControl(best);
  }

  /** Returns the highest-priority current threat point (and a label) or null if calm.
   *  Villages currently being bombed win over allies under fire; ties broken by recency. */
  private pickThreatPoint(): { pos: THREE.Vector3; label: string; severity: 'low' | 'high' } | null {
    const now = simClock.now; // compares against lastDamageAt, which is stamped on the sim clock
    type Best = { pos: THREE.Vector3; label: string; severity: 'low' | 'high'; t: number };
    let best: Best | null = null;
    // Villages — under-attack window 6s, severity high (bombing kills the run).
    for (let i = 0; i < this.gameScene.villages.length; i++) {
      const v = this.gameScene.villages[i];
      if (v.destroyed) continue;
      const age = now - v.lastDamageAt;
      if (age > 6) continue;
      if (!best || v.lastDamageAt > best.t) {
        best = { pos: v.position, label: `VILLAGE ${i + 1} UNDER ATTACK`, severity: 'high', t: v.lastDamageAt };
      }
    }
    // Friendlies — under-fire window 4s, severity low if villages already win.
    if (!best || best.severity === 'low') {
      for (const e of this.entities) {
        if (!e.friendly || e.dead || !e.alive()) continue;
        const age = now - e.lastDamageAt;
        if (age > 4) continue;
        if (!best || e.lastDamageAt > best.t) {
          const label = e.unitLabel ? `${e.unitLabel} TAKING FIRE` : 'ALLY TAKING FIRE';
          best = { pos: e.position, label, severity: 'low', t: e.lastDamageAt };
        }
      }
    }
    return best ? { pos: best.pos, label: best.label, severity: best.severity } : null;
  }

  /** Cycle control to the next/previous allied unit. */
  jumpCycle(dir: 1 | -1) {
    const units = this.controllables();
    if (units.length <= 1) return;
    let idx = units.indexOf(this.controlled);
    if (idx < 0) idx = 0; // controlled unit just died / not yet swept — start from the top
    const next = units[(idx + dir + units.length) % units.length];
    this.takeControl(next);
  }

  /** Jump to a specific unit (radar/map tap, jump bar, crisis button). */
  takeControl(unit: Entity) {
    if (unit === this.controlled || !unit.controllable) return;
    this.controlled.release();
    unit.possess(this.camera);
    this.controlled = unit;
    this.announceUnit(unit);
  }

  /** Push a transient control-scheme hint for the unit just taken over — the controls
   *  silently change between plane / gun / tank, so tell the player what they're driving. */
  announceUnit(unit: Entity) {
    let hint: string;
    if (unit instanceof TownGunner) hint = 'AA GUN · aim with stick · hold FIRE / RT';
    else if (unit instanceof AlliedTank) hint = 'TANK · left stick drives · right stick aims · FIRE / RT';
    else hint = 'FIGHTER · drag / left stick to fly · hold FIRE / RT to shoot';
    useStore.getState().setControlHint(hint);
  }

  /** Called when the controlled unit is destroyed. Hands off to the nearest surviving
   *  ally if any (returns true); returns false to let the caller run the final game-over. */
  onControlledLost(unit: Entity): boolean {
    const others = this.controllables().filter((e) => e !== unit);
    if (others.length === 0) return false;
    let best = others[0];
    let bestD = Infinity;
    for (const e of others) {
      const d = e.position.distanceToSquared(unit.position);
      if (d < bestD) { bestD = d; best = e; }
    }
    best.possess(this.camera);
    this.controlled = best;
    this.announceUnit(best);
    return true;
  }

  /** Toggle the fullscreen tactical map (a live command surface — the sim keeps running). */
  toggleMap() {
    const store = useStore.getState();
    if (store.mapOpen) {
      store.setMapOpen(false);
      store.setMapSelected(null);
      store.setMapCursor(null);
      return;
    }
    // Seed the gamepad cursor over the unit you're flying so it starts somewhere useful.
    this._mapCursor.set(this.controlled.position.x, this.controlled.position.z);
    store.setMapOpen(true, this.buildMapData());
    store.setMapSelected(null);
    store.setMapCursor({ x: this._mapCursor.x, z: this._mapCursor.y });
  }

  private _mapCursor = new THREE.Vector2();
  /** Drive the whole tactical map from the gamepad: left stick moves a cursor, bumpers
   *  cycle unit selection, A acts in context (select / Defend / Attack / Patrol), Y pilots,
   *  B cancels or closes. Mirrors the pointer interactions through the same store + Game
   *  methods so the two input paths stay in sync. */
  private handleMapGamepad(dt: number) {
    const store = useStore.getState();
    const RING = 6000, SNAP = 650, SPEED = 3200;
    // Move the cursor (left stick). y holds world Z; stick +y = up = north = -Z. Only push
    // to the store when it actually changes, so the map doesn't re-render every frame.
    let cursorMoved = false;
    const cs = this.input.getLeftStick();
    if (cs.active) {
      this._mapCursor.x = THREE.MathUtils.clamp(this._mapCursor.x + cs.x * SPEED * dt, -RING, RING);
      this._mapCursor.y = THREE.MathUtils.clamp(this._mapCursor.y - cs.y * SPEED * dt, -RING, RING);
      cursorMoved = true;
    }

    // Bumpers (or Q/E) cycle which of your units is selected, snapping the cursor onto it.
    const units = this.controllables();
    const rb = this.input.pressedEdge(5) || this.input.keyPressedEdge('jumpR', 'KeyE');
    const lb = this.input.pressedEdge(4) || this.input.keyPressedEdge('jumpL', 'KeyQ');
    if ((rb || lb) && units.length > 0) {
      const cur = units.findIndex((u) => u.id === store.mapSelectedId);
      const dir = rb ? 1 : -1;
      const next = units[(Math.max(0, cur) + dir + units.length) % units.length];
      store.setMapSelected(next.id);
      this._mapCursor.set(next.position.x, next.position.z);
      cursorMoved = true;
    }

    if (this.input.pressedEdge(1) || this.input.keyPressedEdge('mapBack', 'Escape')) { // B/Esc — cancel / close
      if (store.mapSelectedId != null) store.setMapSelected(null);
      else { this.toggleMap(); return; }
    }
    if (this.input.pressedEdge(3) || this.input.keyPressedEdge('crisis', 'KeyF')) { // Y/F — pilot the selected unit (or the one under cursor)
      const id = store.mapSelectedId ?? this.nearestFriendlyToCursor(SNAP)?.id ?? null;
      const u = id != null ? this.entities.find((e) => e.id === id) : null;
      if (u && u.controllable) { this.takeControl(u); this.toggleMap(); }
    }
    if (this.input.pressedEdge(0) || this.input.keyPressedEdge('mapAct', 'Enter', 'Space')) { // A/Enter/Space — context action
      if (store.mapSelectedId == null) {
        const u = this.nearestFriendlyToCursor(SNAP);
        if (u) store.setMapSelected(u.id);
      } else {
        // A unit is selected: order it based on whatever's nearest the cursor —
        // village → defend, friendly → escort, enemy → attack, nothing → patrol.
        const village = this.nearestVillageToCursor(SNAP);
        const enemy = this.nearestEnemyToCursor(SNAP);
        const ally = this.nearestFriendlyToCursorEx(SNAP, store.mapSelectedId);
        let order: UnitOrder = { type: 'patrol' };
        let bestD = Infinity;
        if (village && village.d < bestD) { bestD = village.d; order = { type: 'defend', villageIndex: village.index }; }
        if (ally && ally.d < bestD) { bestD = ally.d; order = { type: 'escort', unitId: ally.id }; }
        if (enemy && enemy.d < bestD) { bestD = enemy.d; order = { type: 'attack', targetId: enemy.id }; }
        this.issueOrder(store.mapSelectedId, order);
        store.setMapSelected(null);
      }
    }
    if (cursorMoved) store.setMapCursor({ x: this._mapCursor.x, z: this._mapCursor.y });
  }

  private cursorD2(x: number, z: number): number {
    const dx = x - this._mapCursor.x, dz = z - this._mapCursor.y;
    return dx * dx + dz * dz;
  }
  private nearestFriendlyToCursor(maxD: number): Entity | null {
    let best: Entity | null = null, bd = maxD * maxD;
    for (const e of this.entities) {
      if (!e.controllable || !e.friendly || e.dead || !e.alive()) continue;
      const d = this.cursorD2(e.position.x, e.position.z);
      if (d < bd) { bd = d; best = e; }
    }
    return best;
  }
  private nearestFriendlyToCursorEx(maxD: number, excludeId: number | null): { id: number; d: number } | null {
    let best: { id: number; d: number } | null = null; let bd = maxD * maxD;
    for (const e of this.entities) {
      if (!e.controllable || !e.friendly || e.dead || !e.alive() || e.id === excludeId) continue;
      const d = this.cursorD2(e.position.x, e.position.z);
      if (d < bd) { bd = d; best = { id: e.id, d }; }
    }
    return best;
  }
  private nearestVillageToCursor(maxD: number): { index: number; d: number } | null {
    let best: { index: number; d: number } | null = null; let bd = maxD * maxD;
    this.gameScene.villages.forEach((v, i) => {
      if (v.destroyed) return;
      const d = this.cursorD2(v.position.x, v.position.z);
      if (d < bd) { bd = d; best = { index: i, d }; }
    });
    return best;
  }
  private nearestEnemyToCursor(maxD: number): { id: number; d: number } | null {
    let best: { id: number; d: number } | null = null; let bd = maxD * maxD;
    for (const e of this.findEnemies()) {
      if (!e.alive()) continue;
      const d = this.cursorD2(e.position.x, e.position.z);
      if (d < bd) { bd = d; best = { id: e.id, d }; }
    }
    return best;
  }

  /** Give a unit a standing order from the tactical map. Mobile units (fighters, tanks)
   *  take move orders; fixed AA guns only ever hold their position, so they ignore them. */
  issueOrder(unitId: number, order: UnitOrder) {
    const unit = this.entities.find((e) => e.id === unitId);
    if (!unit || !unit.controllable || unit.dead) return;
    if (unit instanceof TownGunner) return; // emplacements can't relocate
    unit.order = order;
    const store = useStore.getState();
    if (store.tutorial && store.tutorialDone.bomber && unit instanceof AlliedTank
      && order.type === 'defend' && order.villageIndex === this._tutVillageIndex) {
      store.completeTask('order');
      store.setControlHint('ORDER ACKNOWLEDGED · THE FRONT IS YOURS');
      store.setMapOpen(false);
      store.setMapSelected(null);
      store.setMapCursor(null);
    }
  }

  /** Live enemy by id (used by 'attack' orders). Null if it's gone or already wreckage. */
  enemyById(id: number): Entity | null {
    const e = this.entities.find((x) => x.id === id);
    return e && (e.kind === 'enemy' || e.kind === 'zeppelin' || e.kind === 'tank') && !e.dead && e.alive() ? e : null;
  }

  /** A flying thing slamming into the ground: flatten the trees and buildings it lands on and
   *  damage any unit (friend or foe) caught under the wreck. Called from each crashing entity at
   *  the moment it reaches the terrain. `source` is the crasher itself (excluded from its splash). */
  crashImpact(ctx: EntityCtx, pos: THREE.Vector3, radius: number, damage: number, source?: Entity) {
    // Knock down trees in the blast (Forest swap-remove + stumps), with a few leaf/dust puffs
    // (capped so a big airship dropping dozens of trees doesn't spawn dozens of FX at once).
    const felled = this.gameScene.fellTrees(pos.x, pos.z, radius);
    for (let i = 0; i < felled.length && i < 8; i++) {
      new Explosion(felled[i].clone().setY(felled[i].y + 8), 10, 0.7, 'smoke').setDrift(new THREE.Vector3(0, 3, 0)).addTo(ctx);
    }
    // Crater buildings within the radius — individually, like a bomb, so the village degrades.
    const root = this.gameScene.root;
    let buildings = 0;
    for (const v of this.gameScene.villages) {
      if (v.destroyed || pos.distanceTo(v.position) > radius + v.radius) continue;
      for (const b of v.buildings) {
        if (!b.destroyed && pos.distanceTo(b.position) <= radius) { b.destroy(root); buildings++; }
      }
    }
    // Damage units under the impact. Snapshot first: damage() can spawn wreckage into `entities`.
    let units = 0;
    for (const e of this.entities.slice()) {
      if (e === source || e.dead) continue;
      if (e.kind === 'bullet' || e.kind === 'fx') continue;
      if (pos.distanceTo(e.position) > radius) continue;
      e.damage(damage, ctx);
      units++;
    }
    return { trees: felled.length, buildings, units };
  }

  /** One-time teach: the first armor-piercing shell that cracks a boss announces the mechanic. */
  private _apHintShown = false;
  armorPiercingHint() {
    if (this._apHintShown) return;
    this._apHintShown = true;
    useStore.getState().setControlHint('ARMOR-PIERCING HIT — TANK SHELLS CRACK BOSS PLATE 3×');
  }

  /** A shot bounced off an invulnerable boss part: electric-blue spark at the hit (never used for
   *  real damage, so the colour alone says "no effect") + a throttled hint naming the weak point. */
  private _shieldHintAt = -999;
  shieldedFeedback(ctx: EntityCtx, pos: THREE.Vector3, hint: string) {
    new Explosion(pos.clone(), 7, 0.3, 'shield').addTo(ctx);
    if (ctx.now - this._shieldHintAt > 5) {
      this._shieldHintAt = ctx.now;
      useStore.getState().setControlHint(hint);
    }
  }

  // --- UFO encounter: fly past the mountain ring and a steampunk saucer whirs in, shrugs off your
  //     fire, white-outs the sky, and deposits you back in the middle of the battlefield. ---
  private ufo: Ufo | null = null;
  private ufoPhase: 'idle' | 'approach' | 'flash' | 'return' = 'idle';
  private ufoTimer = 0;
  private _ufoScratch = new THREE.Vector3();
  // Fire once you're clear of the ring's OUTER edge (peaks reach out to ~7200) — i.e. you actually
  // got past the mountains, not merely up to them.
  private static readonly UFO_TRIGGER_R = 6900;  // horizontal distance from centre (ring ≈6000±500)
  private static readonly UFO_APPROACH = 3.6;    // s of whir + close-in before the white-out
  private static readonly UFO_FLASH = 0.55;      // s of full white (teleport hidden here)
  private static readonly UFO_FADE = 0.9;        // s to fade white back on the battlefield
  private static readonly UFO_REPEL_R = 46;      // bullets within this of the saucer are deflected

  /** True while a UFO encounter is running — the controlled plane suspends terrain-crash deaths so
   *  a peak can't interrupt the abduction. */
  isUfoActive(): boolean { return this.ufoPhase !== 'idle'; }

  /** Advance the UFO encounter each frame while playing. Idempotent when idle/out-of-range. */
  private updateUfoEncounter(ctx: EntityCtx) {
    const { dt } = ctx;
    const store = useStore.getState();

    if (this.ufoPhase === 'idle') {
      // Trigger only once the human flying a plane has broken THROUGH the ring into the open sky
      // beyond it (not tanks/guns, not the tutorial). The mountains are a real barrier — fly INTO
      // one and you crash; to get past you have to thread a gap or duck under them near the deck.
      // AI wingmen never roam out here, so this is inherently player-only.
      const p = this.controlled;
      if (!(p instanceof PlayerPlane) || store.tutorial) return;
      if (Math.hypot(p.position.x, p.position.z) < Game.UFO_TRIGGER_R) return;
      // Spawn the saucer ahead of the player's nose so it's in view, then it closes in.
      this.ufo = new Ufo();
      this._ufoScratch.set(0, 0, -1).applyQuaternion(p.group.quaternion);
      this.ufo.group.position.copy(p.position).addScaledVector(this._ufoScratch, 320);
      this.ufo.group.position.y += 40;
      this.scene.add(this.ufo.group);
      this.ufoPhase = 'approach';
      this.ufoTimer = 0;
      this.audio.startUfoWhir();
      store.setControlHint('⚙ UNIDENTIFIED CRAFT ⚙');
      return;
    }

    const p = this.controlled;

    if (this.ufoPhase === 'approach') {
      const ufo = this.ufo;
      if (!ufo) { this.cancelUfoEncounter(); return; } // shouldn't happen — bail without a stuck flash
      this.ufoTimer += dt;
      const k = Math.min(1, this.ufoTimer / Game.UFO_APPROACH);
      // Close in on the player: ease the saucer from its spawn point toward hovering just off the
      // nose, descending to eye level, always facing the plane.
      if (p) {
        this._ufoScratch.set(0, 0, -1).applyQuaternion(p.group.quaternion);
        const want = this._ufoScratch.multiplyScalar(70 + (1 - k) * 250).add(p.position);
        want.y += 14 * (1 - k);
        ufo.group.position.lerp(want, 1 - Math.exp(-dt / 0.35));
        ufo.group.lookAt(p.position);
      }
      ufo.update(dt, k);
      this.audio.setUfoWhir(k);
      // Repel all fire: any player round near the hull is deflected in a blue spark — never damaged.
      this.repelBulletsNear(ctx, ufo.group.position);
      // White bleeds in over the last stretch as the whir crescendos.
      store.setWhiteout(k < 0.82 ? 0 : (k - 0.82) / 0.18);
      if (this.ufoTimer >= Game.UFO_APPROACH) {
        this.ufoPhase = 'flash';
        this.ufoTimer = 0;
        store.setWhiteout(1);
        this.audio.stopUfoWhir();
        this.audio.playUfoWarp();
      }
      return;
    }

    if (this.ufoPhase === 'flash') {
      // Full white. On the FIRST flash frame, do the vanish + teleport (hidden by the white).
      if (this.ufo) {
        this.ufo.dispose();
        this.ufo = null;
        this.returnPlayerToBattlefield();
      }
      store.setWhiteout(1);
      this.ufoTimer += dt;
      if (this.ufoTimer >= Game.UFO_FLASH) { this.ufoPhase = 'return'; this.ufoTimer = 0; }
      return;
    }

    if (this.ufoPhase === 'return') {
      // Fade the white back to reveal the player mid-battlefield, saucer gone.
      this.ufoTimer += dt;
      store.setWhiteout(Math.max(0, 1 - this.ufoTimer / Game.UFO_FADE));
      if (this.ufoTimer >= Game.UFO_FADE) { store.setWhiteout(0); this.ufoPhase = 'idle'; }
    }
  }

  /** Deflect (destroy + spark) any player bullet within the saucer's repel field. */
  private repelBulletsNear(ctx: EntityCtx, center: THREE.Vector3) {
    const r2 = Game.UFO_REPEL_R * Game.UFO_REPEL_R;
    for (const e of this.entities) {
      if (e.dead || !(e instanceof Bullet) || e.owner !== 'player') continue;
      if (e.position.distanceToSquared(center) <= r2) {
        e.dead = true;
        new Explosion(e.position.clone(), 6, 0.3, 'shield').addTo(ctx);
      }
    }
  }

  /** Set the flown plane back down in the middle of the battlefield, levelled and re-energised. */
  private returnPlayerToBattlefield() {
    const p = this.controlled;
    if (!(p instanceof PlayerPlane)) return;
    p.group.position.set(0, -120, 0);
    p.group.quaternion.identity();     // level, facing -Z (into the field)
    p.forward.set(0, 0, -1);
    p.resetEnergyState();
    this.audio.stopUfoWhir();
  }

  /** Abort any in-progress encounter (death / restart) so the white-out can't stick. */
  private cancelUfoEncounter() {
    if (this.ufo) { this.ufo.dispose(); this.ufo = null; }
    this.ufoPhase = 'idle';
    this.ufoTimer = 0;
    this.audio.stopUfoWhir();
    useStore.getState().setWhiteout(0);
  }

  /** Live village centre by index (used by 'defend' orders). Null if destroyed/out of range. */
  villageCenter(index: number): THREE.Vector3 | null {
    const v = this.gameScene.villages[index];
    return v && !v.destroyed ? v.position : null;
  }

  /** Build an absolute world-frame snapshot for the tactical map. Unlike the radar (which
   *  is player-relative and rotates), the map uses raw world X/Z so the layout is stable. */
  private buildMapData(): MapData {
    const now = simClock.now; // threat flags compare against sim-clock lastDamageAt
    const friendlies = [] as MapData['friendlies'];
    const enemies = [] as MapData['enemies'];
    const villages = [] as MapData['villages'];
    for (const e of this.entities) {
      if (e.friendly && !e.dead && e.alive()) {
        const kind: FriendlyKind = e instanceof TownGunner ? 'gun' : e instanceof AlliedTank ? 'tank' : 'plane';
        // Resolve the order's target into a world point so the map can draw the plan.
        let order: MapData['friendlies'][number]['order'] = { type: e.order.type };
        if (e.order.type === 'defend') {
          const v = this.villageCenter(e.order.villageIndex);
          if (v) order = { type: 'defend', tx: v.x, tz: v.z };
        } else if (e.order.type === 'escort') {
          const u = this.friendlyById(e.order.unitId);
          if (u) order = { type: 'escort', tx: u.position.x, tz: u.position.z };
        } else if (e.order.type === 'attack') {
          const t = this.enemyById(e.order.targetId);
          if (t) order = { type: 'attack', tx: t.position.x, tz: t.position.z };
        }
        // What it's actually shooting at right now (faint "engaging" line on the map).
        let engage: { x: number; z: number } | undefined;
        if (e.engageTargetId) {
          const t = this.enemyById(e.engageTargetId);
          if (t) engage = { x: t.position.x, z: t.position.z };
        }
        friendlies.push({
          id: e.id, kind, x: e.position.x, z: e.position.z,
          controlled: e === this.controlled, hpFrac: e.hpFrac(),
          threatened: (now - e.lastDamageAt) < 4, label: e.unitLabel || 'UNIT', order, engage,
        });
      }
    }
    for (const e of this.findEnemies()) {
      const kind: 'enemy' | 'zeppelin' | 'tank' =
        e.kind === 'zeppelin' ? 'zeppelin' : e.kind === 'tank' ? 'tank' : 'enemy';
      enemies.push({ id: e.id, kind, x: e.position.x, z: e.position.z, dying: !e.alive() });
    }
    this.gameScene.villages.forEach((v, i) => {
      const total = v.buildings.length;
      const destroyed = v.buildings.filter((b) => b.destroyed).length;
      villages.push({
        index: i, x: v.position.x, z: v.position.z,
        damageFrac: total > 0 ? destroyed / total : 0,
        destroyed: v.destroyed, threatened: !v.destroyed && (now - v.lastDamageAt) < 6,
      });
    });
    return { friendlies, enemies, villages, worldRadius: 6200, ringRadius: 6000 };
  }

  /** Global cease-fire (the "Truce" random event): swallow every bullet/shell at the single choke
   *  point so no unit — enemy, ally, boss, player — actually fires, without touching each site. */
  ceaseFire = false;
  /** Enemy reload multiplier (<1 = they shoot faster). Blood-Red Dawn gives the enemy the sun. */
  enemyFireScale = 1;

  add(e: Entity) {
    // Drop the round (never added to the scene → GC'd; don't dispose, tracer materials are shared).
    if (this.ceaseFire && e.kind === 'bullet') return; // Shell extends Bullet, so this covers both
    this.entities.push(e);
    this.scene.add(e.group);
    this.applyEnvIntensity(e.group); // keep IBL subtle on every spawned unit
  }

  /** Is a boss currently on the field (for gating events that shouldn't interrupt a boss fight)? */
  bossActive(): boolean { return this.entities.some((e) => e.isBoss && !e.dead); }

  remove(e: Entity) {
    e.dispose();
    this.entities = this.entities.filter((x) => x !== e);
  }

  findEnemies(): Entity[] {
    // Exclude `dead` entities so nothing targets a unit that's already destroyed (a few
    // death paths flip `dead` before `alive()` flips, and the sweep is end-of-frame).
    // Still includes dying-but-not-dead bosses so the radar/reticles can grey them out.
    return this.entities.filter((e) => !e.dead && (e.kind === 'enemy' || e.kind === 'zeppelin' || e.kind === 'tank'));
  }

  /** Pull a spawn point radially inward until it's clear of every mountain cone (evaluated at the
   *  point's own altitude, with margin) so nothing ever appears inside a peak. The ring of peaks
   *  surrounds a clear centre, so stepping toward the origin always exits the rock. */
  private clampSpawnClearOfMountains(pos: THREE.Vector3, margin = 60): THREE.Vector3 {
    for (let iter = 0; iter < 40; iter++) {
      let penetration = 0;
      for (const pk of this.gameScene.peaks) {
        if (pos.y >= pk.summitY) continue; // above this peak's tip — never inside it
        const coneR = pk.radius * (pk.summitY - Math.max(pos.y, pk.baseY)) / (pk.summitY - pk.baseY);
        const hd = Math.hypot(pos.x - pk.pos.x, pos.z - pk.pos.z);
        penetration = Math.max(penetration, (coneR + margin) - hd);
      }
      if (penetration <= 0) break;
      const hd = Math.hypot(pos.x, pos.z);
      if (hd < 1) break;
      const s = Math.max(0, (hd - penetration - 8) / hd);
      pos.x *= s; pos.z *= s;
    }
    return pos;
  }

  spawnEnemyNear(from: THREE.Vector3, type: EnemyType = 'standard', distance?: number) {
    // Default spawn range: far enough to be a "speck on the horizon" the radar has to point out,
    // but inside the fog wall (fog far = 10000) and inside the mountain ring.
    const d = distance ?? (1800 + Math.random() * 900);
    const dir = new THREE.Vector3(Math.random() - 0.5, (Math.random() - 0.5) * 0.2, Math.random() - 0.5).normalize();
    const pos = from.clone().add(dir.multiplyScalar(d));
    pos.y = Math.max(50, pos.y);
    this.clampSpawnClearOfMountains(pos); // never inside a mountain (esp. when the player is near the ring)
    const e = new EnemyPlane(pos, type);
    this.add(e);
    return e;
  }

  spawnZeppelin() {
    const angle = Math.random() * Math.PI * 2;
    const radius = 2800;
    const pos = new THREE.Vector3(Math.cos(angle) * radius, 320, Math.sin(angle) * radius);
    const z = new Zeppelin(pos);
    this.add(z);
    return z;
  }

  spawnSkyCarrier() {
    const c = new SkyCarrier();
    this.add(c);
    return c;
  }

  spawnTank() {
    // Spawn just inside the mountain ring, at the world edge — but never inside a peak's base.
    const angle = Math.random() * Math.PI * 2;
    const r = 5400;
    const pos = new THREE.Vector3(Math.cos(angle) * r, 0, Math.sin(angle) * r);
    pos.y = this.gameScene.heightAt(pos.x, pos.z);      // sit on the terrain (cone is widest here)
    this.clampSpawnClearOfMountains(pos, 90);
    pos.y = this.gameScene.heightAt(pos.x, pos.z);      // re-seat after any inward nudge
    const t = new Tank(pos);
    this.add(t);
    return t;
  }

  spawnJuggernaut() {
    const angle = Math.random() * Math.PI * 2;
    const r = 5200;
    const pos = new THREE.Vector3(Math.cos(angle) * r, 0, Math.sin(angle) * r);
    pos.y = this.gameScene.heightAt(pos.x, pos.z);
    this.clampSpawnClearOfMountains(pos, 120);          // the Juggernaut is huge — extra clearance
    pos.y = this.gameScene.heightAt(pos.x, pos.z);
    const j = new Juggernaut(pos);
    this.add(j);
    return j;
  }

  spawnAetherDreadnought() {
    const d = new AetherDreadnought();
    this.add(d);
    return d;
  }

  spawnColossus() {
    const angle = Math.random() * Math.PI * 2;
    const r = 4800;
    const pos = new THREE.Vector3(Math.cos(angle) * r, 0, Math.sin(angle) * r);
    pos.y = this.gameScene.heightAt(pos.x, pos.z); // sit on the terrain, not a fixed plane
    const c = new ClockworkColossus(pos);
    this.add(c);
    return c;
  }

  spawnTeslaBastion() {
    const angle = Math.random() * Math.PI * 2;
    const r = 4800;
    const pos = new THREE.Vector3(Math.cos(angle) * r, 0, Math.sin(angle) * r);
    pos.y = this.gameScene.heightAt(pos.x, pos.z); // sit on the terrain, not a fixed plane
    const b = new TeslaBastion(pos);
    this.add(b);
    return b;
  }

  spawnIronWorm() {
    const angle = Math.random() * Math.PI * 2;
    const r = 5200;
    const pos = new THREE.Vector3(Math.cos(angle) * r, 0, Math.sin(angle) * r);
    pos.y = this.gameScene.heightAt(pos.x, pos.z); // sit on the terrain, not a fixed plane
    const w = new IronWorm(pos);
    this.add(w);
    return w;
  }

  spawnMaelstromEngine() {
    // Mount on a mountain summit — `summitY` is the actual top of the cone, not the
    // 70%-height "peak" point that's still inside the rock.
    const peaks = this.gameScene.peaks;
    const peak = peaks.length > 0 ? peaks[Math.floor(Math.random() * peaks.length)] : null;
    // -8 (not -4) gives a defensive margin against fractal apex jitter (which can lift
    // the visible apex up to ~6u above `summitY` at extreme mountain heights).
    const pos = peak
      ? new THREE.Vector3(peak.pos.x, peak.summitY - 8, peak.pos.z)
      : new THREE.Vector3(2000, 200, 2000);
    const m = new MaelstromEngine(pos);
    this.add(m);
    return m;
  }

  handleResize = () => {
    const w = window.innerWidth, h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    if (this.composer) this.composer.setSize(w, h);
    if (this.fxaaPass) {
      const dpr = this.renderer.getPixelRatio();
      this.fxaaPass.material.uniforms.resolution.value.set(1 / (w * dpr), 1 / (h * dpr));
    }
    if (this.camera) {
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }
  };

  start() {
    if (this.running) return;
    this.running = true;
    this.clock.start();
    this.loop();
  }

  restart() {
    // Remove all entities except... actually, remove everything and re-spawn the player.
    this.cancelUfoEncounter();
    this.randomEvents.reset(this);
    [...this.entities].forEach((e) => this.remove(e));
    // The death sequence detaches the camera to the scene root. Remove it now so the
    // fresh PlayerPlane can own a new camera without orphans lingering in the scene.
    if (this.camera.parent) this.camera.parent.remove(this.camera);
    this.setupCameraAndPlayer();
    this.director.reset();
    // Reset audio-event watchers so the new run doesn't fire stale kill/alarm SFX.
    this._prevKills = 0;
    this._villageAlarmAt = -999;
    this._lastVillageDamage = -999;
    this.resetTutorialMission();
    this.lostUnits.length = 0;
    useStore.getState().resetRun();
    // DEV-ONLY: ?wave=N starts the run on that wave (e.g. ?wave=12 → Tesla Bastion) for boss
    // playtesting. Vite strips the whole branch from production builds, so deployed players
    // always start at wave 1.
    const startWave = (() => {
      if (!import.meta.env.DEV) return 1;
      const raw = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('wave') : null;
      const w = raw ? parseInt(raw, 10) : NaN;
      return Number.isFinite(w) && w >= 1 ? w : 1;
    })();
    useStore.getState().setWave(startWave, simClock.now); // waveIntroAt rides the sim clock (HUD reads it)
  }

  // --- Authored opening training sortie -------------------------------------------------------
  private _tutScoutId = 0;
  private _tutBomberId = 0;
  private _tutTankId = 0;
  private _tutVillageIndex = -1;

  private resetTutorialMission() {
    this._tutScoutId = 0;
    this._tutBomberId = 0;
    this._tutTankId = 0;
    this._tutVillageIndex = -1;
    if (!useStore.getState().tutorial) return;

    // Pick a village that genuinely has an AA gun close by. Reusing the real force layout means
    // crisis-jump teaches the same decision the player will make in the campaign.
    let bestD = Infinity;
    this.gameScene.villages.forEach((v, i) => {
      for (const e of this.entities) {
        if (!(e instanceof TownGunner)) continue;
        const d = e.position.distanceToSquared(v.position);
        if (d < bestD) { bestD = d; this._tutVillageIndex = i; }
      }
    });
  }

  private tutorialTargetAlive(id: number): boolean {
    if (!id) return false;
    const e = this.entities.find((x) => x.id === id);
    return !!e && !e.dead && e.alive();
  }

  private spawnTutorialScout() {
    const pilot = this.controlled instanceof PlayerPlane ? this.controlled : null;
    if (!pilot) return;
    const pos = pilot.position.clone().addScaledVector(pilot.forward, 620).add(new THREE.Vector3(0, 28, 0));
    const scout = new EnemyPlane(pos, 'standard');
    scout.group.quaternion.copy(pilot.group.quaternion);
    scout.makeTrainingTarget(true);
    this.add(scout);
    this._tutScoutId = scout.id;
    useStore.getState().setControlHint('SCOUT AHEAD · HOLD FIRE');
  }

  private spawnTutorialBomber() {
    const village = this.gameScene.villages[this._tutVillageIndex];
    if (!village || !(this.controlled instanceof TownGunner)) return;
    // Place the bomber inside gun range and roughly down the emplacement's initial sightline.
    const pos = this.controlled.position.clone().add(new THREE.Vector3(0, 210, -900));
    const bomber = new EnemyPlane(pos, 'bomber');
    bomber.makeTrainingTarget(false);
    this.add(bomber);
    this._tutBomberId = bomber.id;
    useStore.getState().setControlHint('BOMBER IN RANGE · AIM AND HOLD FIRE');
  }

  private spawnTutorialArmor() {
    const village = this.gameScene.villages[this._tutVillageIndex];
    if (!village) return;
    const away = new THREE.Vector3(village.position.x, 0, village.position.z);
    if (away.lengthSq() < 1) away.set(1, 0, 0); else away.normalize();
    const pos = village.position.clone().addScaledVector(away, 1450);
    pos.y = this.gameScene.heightAt(pos.x, pos.z);
    const tank = new Tank(pos);
    tank.trainingTarget = true;
    this.add(tank);
    this._tutTankId = tank.id;
    useStore.getState().setControlHint('ARMORED CONTACT · OPEN THE TACTICAL MAP');
  }

  /** Advance one lesson at a time. Scripted contacts use the real combat, possession and order
   *  systems, but their weapons are safe and uncrewed allied AI ignores them. */
  private trackTutorial() {
    let store = useStore.getState();
    let done = store.tutorialDone;
    const stick = this.input.getStick();

    // Beat 1: flight check. Reveal one input at a time rather than presenting a wall of controls.
    if (!done.flight) {
      if (!done.steer && stick.active && Math.hypot(stick.x, stick.y) > 0.25) store.completeTask('steer');
      if (!done.climb && stick.active && Math.abs(stick.y) > 0.5) store.completeTask('climb');
      if (!done.boost && (store.boosting || this.input.gamepadBoostAmount() > 0.4 || this.input.keyBoostHeld())) {
        store.completeTask('boost');
      }
      done = useStore.getState().tutorialDone;
      if (done.steer && done.climb && done.boost) {
        store.completeTask('flight');
        store.setControlHint('FLIGHT CHECK COMPLETE');
      }
      return;
    }

    // Beat 2: a straight-flying, unarmed scout gives the player a clean first kill.
    if (!done.scout) {
      if (!this._tutScoutId) this.spawnTutorialScout();
      if (!done.fire && store.isFiring) store.completeTask('fire');
      done = useStore.getState().tutorialDone;
      if (!this.tutorialTargetAlive(this._tutScoutId)) {
        if (done.fire) {
          store.completeTask('scout');
          store.setControlHint('SCOUT DESTROYED · NEW THREAT');
        } else {
          this._tutScoutId = 0;
        }
      }
      return;
    }

    const village = this.gameScene.villages[this._tutVillageIndex];

    // Beat 3: keep one village in an alarm state so crisis-jump selects its nearby AA gun. The
    // bomber only enters once the player has taken the correct seat, then flies a safe attack run.
    if (!done.bomber) {
      if (village) village.lastDamageAt = simClock.now;
      if (!done.gun) {
        const closeGun = this.controlled instanceof TownGunner && !!village
          && this.controlled.position.distanceToSquared(village.position) < 500 * 500;
        if (closeGun) {
          store.completeTask('gun');
          this.spawnTutorialBomber();
        }
        return;
      }
      if (!this._tutBomberId) this.spawnTutorialBomber();
      if (!this.tutorialTargetAlive(this._tutBomberId)) {
        store.completeTask('bomber');
        store.setControlHint('BOMBER DOWN · ARMOR ON THE ROAD');
      }
      return;
    }

    // Beat 4: put a real armor contact on the map, pulse the threatened village, and accept only
    // the useful command: an allied tank ordered to defend that settlement.
    if (!done.order) {
      if (!this._tutTankId) this.spawnTutorialArmor();
      if (village) village.lastDamageAt = simClock.now;
      if (!done.map && store.mapOpen) store.completeTask('map');
    }
  }

  private loop = () => {
    if (!this.running) return;
    requestAnimationFrame(this.loop);
    const dt = Math.min(this.clock.getDelta(), 0.05);
    // Advance the shared SIM clock from the clamped dt so gameplay timing stays locked to motion
    // (both ride this clock; at low FPS the whole sim slows together — see clock.ts). UI/audio
    // below still use wall time (performance.now) on purpose.
    simClock.now += dt;
    const now = simClock.now;
    const ctx: EntityCtx = { game: this, scene: this.scene, dt, now };

    // FX quality setting flips bloom live (the composer skips disabled passes).
    if (this.bloomPass) this.bloomPass.enabled = !useStore.getState().fxLite;

    const phase = useStore.getState().phase;
    if (phase === 'playing') {
      // X/□ opens/closes the map (works in both contexts); Back = mute, L3 = AI autopilot.
      if (this.input.pressedEdge(2) || this.input.keyPressedEdge('map', 'KeyM', 'Tab')) this.toggleMap();
      if (this.input.pressedEdge(8)) useStore.getState().toggleMute();
      if (this.input.pressedEdge(10)) useStore.getState().toggleAi();
    }
    const mapOpen = useStore.getState().mapOpen;

    if (phase === 'playing') {
      if (mapOpen) {
        // Map context: the pad drives the command cursor + orders instead of flying.
        this.handleMapGamepad(dt);
      } else {
        // Flight context: bumpers cycle units (LB/RB or Q/E); Y (or F) crisis-jumps. RT/Space =
        // manual fire (fireHeld), LT/Shift = afterburner — both polled where they're used, not here.
        const rb = this.input.pressedEdge(5) || this.input.keyPressedEdge('jumpR', 'KeyE');
        const lb = this.input.pressedEdge(4) || this.input.keyPressedEdge('jumpL', 'KeyQ');
        if (rb) this.jumpCycle(1); else if (lb) this.jumpCycle(-1);
        // Y crisis-jumps to the top threat. (RT is now manual gun fire — see fireHeld.)
        if (this.input.pressedEdge(3) || this.input.keyPressedEdge('crisis', 'KeyF')) this.jumpToCrisis();
      }
      // The battle runs LIVE even while the map is open — the player just isn't piloting,
      // so the controlled unit flies on autopilot (entities read `mapOpen` from the store).
      for (const e of this.entities) e.update(ctx);
      runCollisions(this.entities, ctx);
      this.director.tick(ctx);
      this.updateUfoEncounter(ctx);
      if (!useStore.getState().tutorial) this.randomEvents.update(this, ctx);
      // Combo is a player-facing scoring window stamped on wall time (registerKill/NearMiss), so
      // tick it on wall time too — it intentionally tracks real seconds, not sim-slowed seconds.
      useStore.getState().tickCombo(performance.now() / 1000);
      if (useStore.getState().tutorial) this.trackTutorial();
    } else if (phase === 'gameover') {
      // still animate fx + falling enemies
      if (this.ufoPhase !== 'idle') this.cancelUfoEncounter(); // don't let a white-out bleed into the crash cam
      for (const e of this.entities) e.update(ctx);
    }

    // sweep dead
    if (this.entities.some((e) => e.dead)) {
      this.entities = this.entities.filter((e) => {
        if (e.dead) {
          // Queue lost friendlies (oldest first) so reinforcements replace whatever died first.
          if (e.friendly && e.controllable) {
            const kind: FriendlyKind = e instanceof TownGunner ? 'gun' : e instanceof AlliedTank ? 'tank' : 'plane';
            const elevate = e instanceof TownGunner ? e.elevate : 0;
            this.lostUnits.push({ kind, label: e.unitLabel, pos: e.position.clone(), elevate });
          }
          e.dispose();
          return false;
        }
        return true;
      });
    }

    this.gameScene.update(ctx);
    // Track village destruction for the HUD + game-over loss condition.
    const total = this.gameScene.villages.length;
    let alive = 0;
    for (const v of this.gameScene.villages) if (!v.destroyed) alive++;
    const store = useStore.getState();
    if (store.villagesAlive !== alive || store.villagesTotal !== total) {
      store.setVillageCounts(alive, total);
    }

    // --- Audio events ---------------------------------------------------------------------------
    // Kill-confirm: the store's kill counter is the single funnel for every enemy death.
    if (store.kills > this._prevKills) {
      this.audio.playKill(store.combo);
      this._prevKills = store.kills;
    }
    // Village under-attack klaxon: fire when a village's last-damage stamp advances (a building just
    // fell), debounced to once every 6s so a strafing/bombing run isn't a wall of sirens.
    let freshestDamage = -999;
    for (const v of this.gameScene.villages) {
      if (!v.destroyed && v.lastDamageAt > freshestDamage) freshestDamage = v.lastDamageAt;
    }
    if (freshestDamage > this._lastVillageDamage) {
      const tnow = simClock.now; // freshestDamage is sim-clock village lastDamageAt
      if (tnow - this._villageAlarmAt > 6) { this.audio.playAlarm(); this._villageAlarmAt = tnow; }
      this._lastVillageDamage = freshestDamage;
    }
    if (alive === 0 && total > 0 && store.phase === 'playing') {
      store.setLossReason('villages');
      store.beginDeath(performance.now() / 1000);
      store.markDeathImpact(performance.now() / 1000);
      store.setPhase('gameover');
    }

    // Tactical map is open: refresh its live world snapshot (throttled) and render the
    // scene behind it. The cockpit HUD (radar/reticles/audio rev) is hidden under the map,
    // so skip those updates. If the run ended while the map was up, drop back to the
    // cockpit so the crash/game-over plays out unobscured.
    if (mapOpen) {
      if (store.phase !== 'playing') {
        store.setMapOpen(false);
      } else {
        this._mapAccum += dt;
        if (this._mapAccum >= 0.1) { this._mapAccum = 0; store.setMapOpen(true, this.buildMapData()); }
        this.composer.render();
        return;
      }
    }

    this.pushControlRoster(store);
    // Engine intensity from the plane's actual integrated rates so AI autopilot maneuvers
    // also rev the engine, not just the player's stick input. Ground units (AA gun, tank)
    // have no aero engine, so the plane engine + slipstream fade out while commanding them.
    const flying = this.controlled.kind === 'player';
    this.audio.update(this.player.getManeuverIntensity(), flying);
    // Positional engine audio for the 3 nearest enemies, with manual Doppler.
    this._playerVel.set(0, 0, -1).applyQuaternion(this.player.group.quaternion).multiplyScalar(78);
    this.audio.updateEnemyEngines(this.entities, this.player.position, this._playerVel);
    this.updateEnemyArrow();
    this.updateEnemyMarks();
    this.updateRadar(dt);
    this.updateAlert();
    this.composer.render();
  };

  /** Profiling: time the FULL post-FX composer render over `frames`, forcing a GPU sync each frame
   *  (1px readback) so async GPU work can't hide past the timer. Returns median/mean ms + fps. This
   *  measures the real on-GPU frame cost INCLUDING bloom/MSAA/vignette, which the diag harness (a
   *  direct renderer.render, no composer) cannot. Combine with the ?msaa=0/nobloom=1/novig=1 flags
   *  to isolate each post-FX stage's cost. */
  benchComposer(frames = 300): { frames: number; medianMs: number; meanMs: number; fps: number } {
    const gl = this.renderer.getContext();
    const px = new Uint8Array(4);
    for (let i = 0; i < 10; i++) { this.composer.render(); gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); }
    const times: number[] = [];
    for (let i = 0; i < frames; i++) {
      const t0 = performance.now();
      this.composer.render();
      gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
      times.push(performance.now() - t0);
    }
    times.sort((a, b) => a - b);
    const median = times[times.length >> 1];
    const mean = times.reduce((s, t) => s + t, 0) / times.length;
    return { frames, medianMs: +median.toFixed(3), meanMs: +mean.toFixed(3), fps: +(1000 / median).toFixed(1) };
  }

  private _rosterSig = '';
  /** Publish the jumpable-unit roster to the store for the HUD — only when it actually
   *  changes (roster membership, labels, controlled unit, or a coarse HP bucket) so we
   *  don't thrash React every frame. */
  private pushControlRoster(store: ReturnType<typeof useStore.getState>) {
    const units = this.controllables();
    const list = units.map((u) => {
      const kind: FriendlyKind = u instanceof TownGunner ? 'gun' : u instanceof AlliedTank ? 'tank' : 'plane';
      let orderLabel = 'PATROL · ENGAGE AT WILL';
      if (u.order.type === 'defend') {
        orderLabel = `DEFEND · VILLAGE ${u.order.villageIndex + 1}`;
      } else if (u.order.type === 'escort') {
        const target = this.friendlyById(u.order.unitId);
        orderLabel = `ESCORT · ${target?.unitLabel || 'FORMATION'}`;
      } else if (u.order.type === 'attack') {
        const target = this.enemyById(u.order.targetId);
        const contact = target?.kind === 'tank' ? 'ARMOR' : target?.kind === 'zeppelin' ? 'AIRSHIP' : 'AIR CONTACT';
        orderLabel = `ATTACK · ${contact}`;
      } else if (kind === 'gun') {
        orderLabel = 'DEFEND · FIXED EMPLACEMENT';
      }
      return {
        id: u.id,
        label: u.unitLabel || 'UNIT',
        hpFrac: Math.round(u.hpFrac() * 8) / 8, // bucket to eighths
        kind,
        order: u.order.type,
        orderLabel,
      };
    });
    const controlledId = this.controlled && !this.controlled.dead ? this.controlled.id : 0;
    // Ground guns get a gunsight crosshair; planes/tanks use their own framing.
    const reticle: 'none' | 'crosshair' =
      (this.controlled instanceof TownGunner || this.controlled instanceof AlliedTank) ? 'crosshair' : 'none';
    const sig = controlledId + '|' + reticle + '|' + list.map((u) => `${u.id}:${u.label}:${u.hpFrac}:${u.orderLabel}`).join(',');
    if (sig === this._rosterSig) return;
    this._rosterSig = sig;
    store.setControlUnits(list, controlledId, reticle);
  }

  private _playerVel = new THREE.Vector3();

  private _arrowVec = new THREE.Vector3();
  private _fwdVec = new THREE.Vector3();
  private _markVec = new THREE.Vector3();
  private _toEnemy = new THREE.Vector3();
  private _fwdH = new THREE.Vector3();
  private _rightH = new THREE.Vector3();
  private _toE = new THREE.Vector3();
  private _partWorld = new THREE.Vector3();
  private updateEnemyMarks() {
    const store = useStore.getState();
    const marks = store.enemyMarks;
    marks.length = 0;
    const enemies = this.findEnemies();
    if (enemies.length === 0) return;
    // Camera facing drives the reticle cone read-out so it tracks where the player is
    // actually looking/aiming, including ground units whose body doesn't face the target.
    this.camera.getWorldDirection(this._fwdVec);

    const pushMark = (worldPos: THREE.Vector3, baseId: number, kind: 'enemy' | 'zeppelin' | 'tank', dying: boolean, partTag: string | null) => {
      this._markVec.copy(worldPos).project(this.camera);
      const inFrontZ = this._markVec.z >= -1 && this._markVec.z <= 1;
      this._toEnemy.copy(worldPos).sub(this.player.position);
      const dist = this._toEnemy.length();
      if (dist > 0) this._toEnemy.multiplyScalar(1 / dist);
      const dot = THREE.MathUtils.clamp(this._toEnemy.dot(this._fwdVec), -1, 1);
      const angleDeg = (Math.acos(dot) * 180) / Math.PI;
      const visible = inFrontZ && Math.abs(this._markVec.x) < 1 && Math.abs(this._markVec.y) < 1 && dot > 0;
      const t = THREE.MathUtils.clamp((dist - 150) / (900 - 150), 0, 1);
      const coneDeg = 28 + (9 - 28) * t;
      const inCone = visible && angleDeg <= coneDeg && dist <= 900;
      // Encode the part tag into a stable id so React keys stay unique (boss + part).
      // Full-string hash so e.g. 'gearLS' and 'gearRS' don't collide.
      let id = baseId;
      if (partTag) {
        let h = 0;
        for (let i = 0; i < partTag.length; i++) h = ((h << 5) - h + partTag.charCodeAt(i)) | 0;
        id = baseId * 1000003 + (h & 0xffff);
      }
      marks.push({ id, kind, ndcX: this._markVec.x, ndcY: this._markVec.y, dist, visible, inCone: inCone && !dying, dying });
    };

    for (const e of enemies) {
      const isMultiPart = !!(e.hitParts && e.hitParts.length > 0 && (e.kind === 'zeppelin' || e.kind === 'tank'));
      if (isMultiPart && e.hitParts) {
        // Emit one mark per surviving sub-target so the player can see what's hittable.
        for (const part of e.hitParts) {
          part.object.getWorldPosition(this._partWorld);
          pushMark(this._partWorld, e.id, e.kind === 'zeppelin' ? 'zeppelin' : 'tank', !e.alive(), part.id);
        }
      } else {
        pushMark(e.position, e.id, e.kind === 'zeppelin' ? 'zeppelin' : (e.kind === 'tank' ? 'tank' : 'enemy'), !e.alive(), null);
      }
    }
  }

  private _mapAccum = 0;
  private _radarAccum = 0;
  private updateRadar(dt: number) {
    this._radarAccum += dt;
    if (this._radarAccum < 0.08) return; // ~12 Hz update
    this._radarAccum = 0;
    const store = useStore.getState();
    const enemies = this.findEnemies();
    const blips = store.radarBlips;
    const friendlies = store.radarFriendlies;
    const radarVillages = store.radarVillages;
    blips.length = 0;
    friendlies.length = 0;
    radarVillages.length = 0;
    // Build a yaw-only horizontal frame: forward projected to the XZ plane, and the
    // corresponding right vector. Roll/pitch are ignored so the radar stays flat. Use the
    // camera's facing (not the entity's) so it's correct for ground units too, where the
    // gun/turret aims independently of the body.
    this.camera.getWorldDirection(this._fwdH);
    this._fwdH.y = 0;
    const l = this._fwdH.length();
    if (l < 1e-6) this._fwdH.set(0, 0, -1);
    else this._fwdH.multiplyScalar(1 / l);
    this._rightH.set(-this._fwdH.z, 0, this._fwdH.x);

    const project = (worldPos: THREE.Vector3): { rx: number; rz: number } => {
      this._toE.copy(worldPos).sub(this.player.position);
      return { rx: this._toE.dot(this._rightH), rz: this._toE.dot(this._fwdH) };
    };

    // Enemies (unchanged contract).
    for (const e of enemies) {
      const p = project(e.position);
      const kind: 'enemy' | 'zeppelin' | 'tank' =
        e.kind === 'zeppelin' ? 'zeppelin' :
        e.kind === 'tank' ? 'tank' : 'enemy';
      blips.push({ kind, rx: p.rx, rz: p.rz, dying: !e.alive() });
    }
    // Friendlies — colour-coded by type so the radar shows your whole force.
    const now = simClock.now; // "threatened" flags compare against sim-clock lastDamageAt
    for (const e of this.entities) {
      if (!e.friendly || e.dead || !e.alive()) continue;
      const kind: FriendlyKind =
        e instanceof TownGunner ? 'gun' :
        e instanceof AlliedTank ? 'tank' : 'plane';
      const p = project(e.position);
      friendlies.push({
        kind, rx: p.rx, rz: p.rz,
        controlled: e === this.controlled,
        hpFrac: e.hpFrac(),
        threatened: (now - e.lastDamageAt) < 4,
      });
    }
    // Villages — show damage state + flag any that are actively being bombed.
    for (const v of this.gameScene.villages) {
      const p = project(v.position);
      const total = v.buildings.length;
      const destroyed = v.buildings.filter((b) => b.destroyed).length;
      const damageFrac = total > 0 ? destroyed / total : 0;
      radarVillages.push({
        rx: p.rx, rz: p.rz,
        damageFrac, destroyed: v.destroyed,
        threatened: !v.destroyed && (now - v.lastDamageAt) < 6,
      });
    }
    store.pushRadarTick();
  }

  /** Build the directional threat banner for the HUD. Recomputes off the same per-frame
   *  threat scan that drives the crisis-jump button so the player can see the same target
   *  the button would send them to. */
  private updateAlert() {
    const threat = this.pickThreatPoint();
    if (!threat) { useStore.getState().setAlert(null); return; }
    // Project the threat point into camera space and decide screen-relative angle.
    this.camera.getWorldPosition(this._camPos);
    this.camera.getWorldDirection(this._camFwd);
    this._camRight.setFromMatrixColumn(this.camera.matrixWorld, 0);
    this._camUp.setFromMatrixColumn(this.camera.matrixWorld, 1);
    this._toEnemy.copy(threat.pos).sub(this._camPos);
    const fwdDot = this._toEnemy.dot(this._camFwd);
    const inFront = fwdDot > 0;
    let ax = this._toEnemy.dot(this._camRight);
    let ay = this._toEnemy.dot(this._camUp);
    const mag = Math.hypot(ax, ay);
    if (mag < 1e-4) { ax = -1; ay = 0; } else { ax /= mag; ay /= mag; }
    const angleRad = Math.atan2(ay, ax);
    useStore.getState().setAlert({
      label: threat.label, angleRad, inFront, severity: threat.severity,
    });
  }

  private _camPos = new THREE.Vector3();
  private _camFwd = new THREE.Vector3();
  private _camRight = new THREE.Vector3();
  private _camUp = new THREE.Vector3();
  private updateEnemyArrow() {
    // Don't point the arrow at wreckage — only at actual threats.
    const enemies = this.findEnemies().filter((e) => e.alive());
    if (enemies.length === 0) {
      useStore.getState().setEnemyArrow({ any: false, offscreen: false, angle: 0, normDist: 1 });
      return;
    }
    // Nearest enemy
    const playerPos = this.player.position;
    let nearest = enemies[0];
    let nearestDistSq = Infinity;
    for (const e of enemies) {
      const d2 = e.position.distanceToSquared(playerPos);
      if (d2 < nearestDistSq) { nearestDistSq = d2; nearest = e; }
    }
    const dist = Math.sqrt(nearestDistSq);
    const normDist = Math.min(1, dist / 2500);

    // Robust "in front vs behind" check using camera forward — project() is unreliable
    // for points behind the camera (it flips signs through the w-divide).
    this.camera.getWorldPosition(this._camPos);
    this.camera.getWorldDirection(this._camFwd);
    this._camRight.setFromMatrixColumn(this.camera.matrixWorld, 0); // world-space camera right
    this._camUp.setFromMatrixColumn(this.camera.matrixWorld, 1);    // world-space camera up

    this._toEnemy.copy(nearest.position).sub(this._camPos);
    const fwdDot = this._toEnemy.dot(this._camFwd); // >0 = in front of camera
    const inFront = fwdDot > 0;

    // NDC projection — only trust xy when the enemy is actually in front.
    this._arrowVec.copy(nearest.position).project(this.camera);
    const onScreen = inFront && Math.abs(this._arrowVec.x) <= 1 && Math.abs(this._arrowVec.y) <= 1;

    if (onScreen) {
      useStore.getState().setEnemyArrow({ any: true, offscreen: false, angle: 0, normDist });
      return;
    }

    // Compute an angle in screen-space (0 = right, +π/2 = up) that points toward the enemy.
    let ax: number, ay: number;
    if (inFront) {
      ax = this._arrowVec.x;  // NDC x: + = right
      ay = this._arrowVec.y;  // NDC y: + = up (screen coords up = small y, but NDC inverts that for us)
    } else {
      // Behind the camera — project the to-enemy vector onto camera's right/up axes.
      // This gives which SIDE of the screen to put the arrow on so that turning that way
      // brings the enemy back into view fastest.
      ax = this._toEnemy.dot(this._camRight);
      ay = this._toEnemy.dot(this._camUp);
      const mag = Math.hypot(ax, ay);
      if (mag < 0.001) {
        // Directly behind (degenerate) — pick a side arbitrarily.
        ax = -1; ay = 0;
      } else {
        ax /= mag; ay /= mag;
      }
    }
    const angle = Math.atan2(ay, ax);
    useStore.getState().setEnemyArrow({ any: true, offscreen: true, angle, normDist });
  }
}
